import React, { useState, useEffect } from 'react';
import { FileText, MessageSquare, Calendar, TrendingUp, AlertTriangle, FilePlus2, FileEdit, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { config } from '../utils/config';
import { formatSubscriptionStatus } from '../utils/formatters';
import Modal from './ui/Modal';

interface Subscription {
  plan_type: string;
  status: string;
  is_trial: boolean;
  trial_end?: string;
  documents_used: number;
  documents_limit: number;
  ai_queries_used: number;
  ai_queries_limit: number;
  invoices_created: number;
  invoices_created_limit: number;
  invoices_edited: number;
  invoices_edited_limit: number;
  invoice_downloads: number;
  invoice_downloads_limit: number;
  current_period_end?: string;
  // When the usage counters roll over. Not the same as current_period_end,
  // which is the Stripe billing date and may be unset or in the past.
  usage_reset_at?: string;
}

interface AccountStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountStatusModal: React.FC<AccountStatusModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchSubscription();
    }
  }, [isOpen]);

  const fetchSubscription = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(config.endpoints.subscription.status);
      setSubscription(response.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const getProgressPercentage = (used: number, limit: number) => {
    if (limit <= 0) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-orange-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getWarningMessage = (percentage: number) => {
    if (percentage >= 100) {
      return <p className="text-xs text-red-400 dark:text-red-300 mt-1 font-semibold">Limit reached</p>;
    } else if (percentage >= 80) {
      return <p className="text-xs text-orange-400 dark:text-orange-300 mt-1">Almost at limit</p>;
    }
    return null;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isTrialExpired = subscription?.status === 'expired' ||
    (subscription?.is_trial && subscription?.trial_end && new Date(subscription.trial_end) < new Date());

  const metrics = subscription ? [
    {
      icon: <FileText className="w-4 h-4" />,
      label: 'Documents',
      used: subscription.documents_used,
      limit: subscription.documents_limit,
    },
    {
      icon: <MessageSquare className="w-4 h-4" />,
      label: 'AI Queries',
      used: subscription.ai_queries_used,
      limit: subscription.ai_queries_limit,
    },
    {
      icon: <FilePlus2 className="w-4 h-4" />,
      label: 'Invoice Creations',
      used: subscription.invoices_created,
      limit: subscription.invoices_created_limit,
    },
    {
      icon: <FileEdit className="w-4 h-4" />,
      label: 'Invoice Edits',
      used: subscription.invoices_edited,
      limit: subscription.invoices_edited_limit,
    },
    {
      icon: <Download className="w-4 h-4" />,
      label: 'PDF Downloads',
      used: subscription.invoice_downloads,
      limit: subscription.invoice_downloads_limit,
    },
  ] : [];

  return (
    <Modal open onClose={onClose} size="sm" title="Account Status">
        <div className="space-y-5">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-coral-500/30 border-t-coral-500 rounded-full animate-spin"></div>
            </div>
          ) : subscription ? (
            <>
              {/* Trial Expired Banner */}
              {isTrialExpired && (
                <div className="bg-red-500/10 border-2 border-red-500/40 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-red-600 dark:text-red-300 mb-1">Trial Expired</h3>
                      <p className="text-sm text-red-500 dark:text-red-200 mb-3">
                        Your free trial has ended. Upgrade to continue using all features.
                      </p>
                      <button
                        onClick={() => { navigate('/pricing'); onClose(); }}
                        className="w-full px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors font-semibold flex items-center justify-center gap-2 text-sm"
                      >
                        <TrendingUp className="w-4 h-4" />
                        Upgrade Now to Continue
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan Info */}
              <div className="bg-coral-50 dark:bg-coral-500/10 border border-coral-200 dark:border-coral-500/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-coral-600 dark:text-coral-400 uppercase tracking-wide">Current Plan</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    subscription.status === 'active' || subscription.status === 'trialing'
                      ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300'
                  }`}>
                    {isTrialExpired ? 'Trial Expired' : formatSubscriptionStatus(subscription.status)}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-stone-900 dark:text-white capitalize">
                  {subscription.plan_type.replace('_', ' ')}
                </h3>
                {subscription.is_trial && subscription.trial_end && (
                  <p className={`text-sm mt-0.5 ${isTrialExpired ? 'text-red-600 dark:text-red-300 font-semibold' : 'text-coral-600 dark:text-foreground-muted'}`}>
                    {isTrialExpired ? 'Trial expired: ' : 'Trial ends: '}{formatDate(subscription.trial_end)}
                  </p>
                )}
              </div>

              {/* Usage Stats */}
              <div>
                <h4 className="text-xs font-semibold text-stone-500 dark:text-coral-400 uppercase tracking-wide mb-3">
                  {subscription.is_trial ? 'Total Usage (Trial)' : 'Monthly Usage'}
                </h4>
                <div className="space-y-3">
                  {metrics.map(({ icon, label, used, limit }) => {
                    const pct = getProgressPercentage(used, limit);
                    return (
                      <div key={label}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2 text-coral-600 dark:text-foreground-muted">
                            {icon}
                            <span className="text-sm font-medium">{label}</span>
                          </div>
                          <span className="text-sm text-stone-700 dark:text-foreground-secondary tabular-nums">
                            {used} / {limit}
                          </span>
                        </div>
                        <div className="w-full bg-stone-200 dark:bg-white/15 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(pct)}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {getWarningMessage(pct)}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Next Reset — driven by usage_reset_at, which tracks the counters'
                  own 30-day clock. It used to show current_period_end (the Stripe
                  billing date), which sits in the past for accounts that never
                  billed through Stripe. */}
              {!subscription.is_trial && subscription.usage_reset_at && (
                <div className="flex items-center gap-2 text-sm text-coral-700 dark:text-foreground-muted bg-coral-50 dark:bg-coral-500/5 rounded-lg p-3">
                  <Calendar className="w-4 h-4 shrink-0" />
                  <span>
                    Next reset:{' '}
                    <span className="text-coral-800 dark:text-foreground-secondary font-medium">
                      {formatDate(subscription.usage_reset_at)}
                    </span>
                  </span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => { navigate('/pricing'); onClose(); }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-coral-500 hover:bg-coral-600 text-white rounded-lg transition-colors font-medium text-sm"
                >
                  <TrendingUp className="w-4 h-4" />
                  View Pricing
                </button>
                <button
                  onClick={onClose}
                  className="px-4 py-2.5 bg-stone-100 dark:bg-coral-500/10 hover:bg-stone-200 dark:hover:bg-coral-500/20 border border-stone-300 dark:border-coral-500/30 text-stone-700 dark:text-foreground-muted rounded-lg transition-colors text-sm"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-stone-500 dark:text-coral-400">No subscription data available</p>
            </div>
          )}
        </div>
    </Modal>
  );
};

export default AccountStatusModal;
